import { useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { useEscapeKey, useOnClickOutside } from '../lib/hooks';
import styles from './Popover.module.css';

/**
 * Anchored panel. `trigger` is a render function receiving
 * { open, toggle, close, triggerProps } so any element can open it.
 * placement: bottom-start | bottom-end | bottom | top-start | top-end
 */
export function Popover({
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  placement = 'bottom-start',
  className,
  panelClassName,
  role = 'dialog',
  label,
  matchWidth = false,
}) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  const setOpen = (next) => {
    if (controlledOpen === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };
  const rootRef = useRef(null);

  useOnClickOutside(rootRef, () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  const api = {
    open,
    toggle: () => setOpen(!open),
    close: () => setOpen(false),
    triggerProps: { 'aria-expanded': open, 'aria-haspopup': role },
  };

  return (
    <div ref={rootRef} className={cn(styles.root, className)}>
      {trigger(api)}
      {open && (
        <div
          role={role}
          aria-label={label}
          className={cn(styles.panel, styles[placement], matchWidth && styles.matchWidth, panelClassName)}
        >
          {typeof children === 'function' ? children(api) : children}
        </div>
      )}
    </div>
  );
}

/**
 * Dropdown menu. items: [{ key, label, icon?, onSelect?, to?, href?, danger?, divider? }]
 * `renderItem` lets callers wrap items in router links.
 */
export function Menu({ trigger, items, placement = 'bottom-end', label = 'Menu', header, className, renderLink }) {
  return (
    <Popover trigger={trigger} placement={placement} role="menu" label={label} className={className} panelClassName={styles.menu}>
      {({ close }) => (
        <>
          {header && <div className={styles.menuHeader}>{header}</div>}
          {items.map((item, index) => {
            if (item.divider) return <div key={`d${index}`} className={styles.divider} role="separator" />;
            const content = (
              <>
                {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                <span>{item.label}</span>
              </>
            );
            const className = cn(styles.item, item.danger && styles.danger);
            if ((item.to || item.href) && renderLink) {
              return (
                <span key={item.key} role="none" onClick={close}>
                  {renderLink(item, className, content)}
                </span>
              );
            }
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={className}
                onClick={() => {
                  close();
                  item.onSelect?.();
                }}
              >
                {content}
              </button>
            );
          })}
        </>
      )}
    </Popover>
  );
}
